import { Check, Pencil, Trash2, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { formatCents, formatDataAmount } from '@/lib/formatters';
import { type IProposalCartItem, NegotiationType } from '@/types/commercial/cart';
import { originalOrUnit, subtotalOrDiscountedFallback } from '@/utils/commercial/cart-price';

type DiscountField = 'discountPercent' | 'connectivityDiscountPercent';

export interface StepCartItemProps {
  item: IProposalCartItem;
  nameLookup: Map<string, string>;
  connPriceLookup: Map<string, number>;
  onEdit: () => void;
  onRemove: () => void;
  onUpdateDiscount: (field: DiscountField, value: number) => void;
  isRemoving: boolean;
  isUpdatingDiscount: boolean;
}

function InlineDiscountBadge({
  value,
  field,
  onSave,
  disabled,
}: {
  value: number;
  field: DiscountField;
  onSave: (field: DiscountField, value: number) => void;
  disabled: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState(String(value));
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setInputValue(String(value));
  }, [value]);

  const handleOpen = (nextOpen: boolean) => {
    setOpen(nextOpen);
    if (nextOpen) {
      setInputValue(String(value));
      setTimeout(() => inputRef.current?.select(), 0);
    }
  };

  const commit = () => {
    const parsed = Number.parseInt(inputValue, 10);
    if (Number.isNaN(parsed) || parsed < 0 || parsed > 100) {
      setInputValue(String(value));
      setOpen(false);
      return;
    }
    if (parsed !== value) {
      onSave(field, parsed);
    }
    setOpen(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      commit();
    }
    if (e.key === 'Escape') {
      setInputValue(String(value));
      setOpen(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={handleOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="rounded border border-gray-300 bg-gray-100 px-2 py-0.5 text-sm font-medium text-gray-800 transition-colors hover:bg-gray-200 focus:outline-none focus:ring-1 focus:ring-blue-400 disabled:cursor-not-allowed disabled:opacity-50"
          disabled={disabled}
        >
          {value}%
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="flex w-auto items-center gap-1 p-2"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <Input
          ref={inputRef}
          type="number"
          min={0}
          max={100}
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          className="h-7 w-16 text-sm [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
        />
        <span className="text-sm text-muted-foreground">%</span>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-6 w-6 text-green-600 hover:text-green-700"
          onClick={commit}
          disabled={disabled}
        >
          <Check className="h-3.5 w-3.5" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-6 w-6 text-muted-foreground hover:text-destructive"
          onClick={() => {
            setInputValue(String(value));
            setOpen(false);
          }}
        >
          <X className="h-3.5 w-3.5" />
        </Button>
      </PopoverContent>
    </Popover>
  );
}

function renderWithDiscount(original: string, discounted: string, hasDiscount: boolean) {
  if (!hasDiscount) return <>{discounted}</>;
  return (
    <>
      {original}
      <span className="text-gray-400 mx-1">|</span>
      <span className="text-green-600">{discounted}</span>
    </>
  );
}

function formatQuantityRange(range: IProposalCartItem['appliedRange']) {
  if (!range) return null;
  if (range.to === null) return null;
  return `(de ${range.from} até ${range.to} itens)`;
}

function renderLabelWithRange(label: string, rangeLabel: string | null) {
  return (
    <>
      {label}
      {rangeLabel && (
        <span className="ml-1 text-xs text-muted-foreground whitespace-nowrap">{rangeLabel}</span>
      )}
    </>
  );
}

type PriceKey = 'cash' | 'installment' | 'monthly' | 'yearly' | 'fixed';

interface PriceDim {
  displayUnit: number;
  discountedUnit: number;
  originalSub: number;
  subtotal: number;
  hasUnit: boolean;
  hasSub: boolean;
}

function computePriceDim(params: {
  originalInCents: number;
  unitInCents: number;
  quantity: number;
  discountPercent: number;
  subtotalInCents: number;
  useUnitDisplaySource?: boolean;
}): PriceDim {
  const {
    originalInCents,
    unitInCents,
    quantity,
    discountPercent,
    subtotalInCents,
    useUnitDisplaySource = false,
  } = params;

  const discountFactor = (100 - discountPercent) / 100;
  const original = originalOrUnit(originalInCents, unitInCents);
  const displayUnit = useUnitDisplaySource ? (unitInCents > 0 ? unitInCents : original) : original;

  return {
    displayUnit,
    discountedUnit: Math.round(displayUnit * discountFactor),
    originalSub: original * quantity,
    subtotal: subtotalOrDiscountedFallback(subtotalInCents, original, quantity, discountPercent),
    hasUnit: (unitInCents ?? 0) > 0,
    hasSub:
      subtotalOrDiscountedFallback(subtotalInCents, original, quantity, discountPercent) > 0 ||
      original * quantity > 0,
  };
}

function resolvePriceLabel(dims: Record<PriceKey, PriceDim>, mode: 'unit' | 'sub'): string {
  const isActive = (key: PriceKey) => (mode === 'unit' ? dims[key].hasUnit : dims[key].hasSub);

  if (isActive('cash') && isActive('installment')) return 'à vista / a prazo';
  if (isActive('cash')) return 'à vista';
  if (isActive('installment')) return 'a prazo';
  if (isActive('monthly')) return 'mensal';
  if (isActive('yearly')) return 'anual';
  return 'fixo';
}

function resolvePriceDisplay(
  dims: Record<PriceKey, PriceDim>,
  mode: 'unit' | 'sub',
  hasDiscount: boolean,
): React.ReactNode {
  const getPair = (key: PriceKey) => {
    const dim = dims[key];
    return mode === 'unit'
      ? { o: formatCents(dim.displayUnit), d: formatCents(dim.discountedUnit), on: dim.hasUnit }
      : { o: formatCents(dim.originalSub), d: formatCents(dim.subtotal), on: dim.hasSub };
  };

  const cash = getPair('cash');
  const inst = getPair('installment');

  if (cash.on && inst.on) {
    return renderWithDiscount(`${cash.o} / ${inst.o}`, `${cash.d} / ${inst.d}`, hasDiscount);
  }

  const priority: PriceKey[] = ['cash', 'installment', 'monthly', 'yearly', 'fixed'];
  for (const key of priority) {
    const pair = getPair(key);
    if (pair.on) return renderWithDiscount(pair.o, pair.d, hasDiscount);
  }

  const fixed = getPair('fixed');
  return renderWithDiscount(fixed.o, fixed.d, hasDiscount);
}

export function StepCartItem({
  item,
  nameLookup,
  connPriceLookup,
  onEdit,
  onRemove,
  onUpdateDiscount,
  isRemoving,
  isUpdatingDiscount,
}: StepCartItemProps) {
  const itemName = nameLookup.get(item.productId) ?? item.productId;
  const hasConnectivity = !!item.connectivityDetails;
  const conn = item.connectivityDetails;
  const quantityRangeLabel = formatQuantityRange(item.appliedRange);
  const hasDiscount = item.discountPercent > 0;
  const monthlyDiscountPercent =
    item.negotiationType === NegotiationType.SALE_WITH_SIM_CARD
      ? item.connectivityDiscountPercent
      : item.discountPercent;

  const dims: Record<PriceKey, PriceDim> = {
    cash: computePriceDim({
      originalInCents: item.originalPriceCashInCents,
      unitInCents: item.unitPriceCashInCents,
      quantity: item.quantity,
      discountPercent: item.discountPercent,
      subtotalInCents: item.subtotalCashInCents,
      useUnitDisplaySource: true,
    }),
    installment: computePriceDim({
      originalInCents: item.originalPriceInstallmentInCents,
      unitInCents: item.unitPriceInstallmentInCents,
      quantity: item.quantity,
      discountPercent: item.discountPercent,
      subtotalInCents: item.subtotalInstallmentInCents,
      useUnitDisplaySource: true,
    }),
    monthly: computePriceDim({
      originalInCents: item.originalPriceMonthlyInCents,
      unitInCents: item.unitPriceMonthlyInCents,
      quantity: item.quantity,
      discountPercent: monthlyDiscountPercent,
      subtotalInCents: item.subtotalMonthlyInCents,
    }),
    yearly: computePriceDim({
      originalInCents: item.originalPriceYearlyInCents,
      unitInCents: item.unitPriceYearlyInCents,
      quantity: item.quantity,
      discountPercent: item.discountPercent,
      subtotalInCents: item.subtotalYearlyInCents,
    }),
    fixed: computePriceDim({
      originalInCents: item.originalPriceFixedInCents,
      unitInCents: item.unitPriceFixedInCents,
      quantity: item.quantity,
      discountPercent: item.discountPercent,
      subtotalInCents: item.subtotalFixedInCents,
    }),
  };

  const hasAnyUnitPrice = Object.values(dims).some((d) => d.hasUnit);
  const hasAnySubtotal = Object.values(dims).some((d) => d.hasSub);
  const unitLabel = resolvePriceLabel(dims, 'unit');
  const subLabel = resolvePriceLabel(dims, 'sub');
  const unitPriceDisplay = resolvePriceDisplay(dims, 'unit', hasDiscount);
  const subPriceDisplay = resolvePriceDisplay(dims, 'sub', hasDiscount);

  const connMonthlyUnitPrice =
    (item.unitPriceMonthlyInCents ?? 0) > 0
      ? item.unitPriceMonthlyInCents
      : (connPriceLookup.get(item.productId) ?? 0);
  const hasConnMonthlyPrice = hasConnectivity && connMonthlyUnitPrice > 0;
  const connMonthlyDiscountedUnit = Math.round(
    connMonthlyUnitPrice * ((100 - monthlyDiscountPercent) / 100),
  );
  const connMonthlySubtotal = subtotalOrDiscountedFallback(
    item.subtotalMonthlyInCents,
    connMonthlyUnitPrice,
    item.quantity,
    monthlyDiscountPercent,
  );

  return (
    <div>
      <div className="rounded-lg border border-gray-200 bg-white shadow-sm overflow-hidden">
        {/* Linha Produto */}
        <div className="grid grid-cols-[45%_1fr_auto] items-center bg-gray-50 px-4 py-2 border-b border-gray-200 divide-x divide-gray-200">
          <span className="font-semibold text-gray-900 pr-4">{itemName}</span>
          <div className="flex items-center gap-2 pl-4">
            <span className="text-sm font-medium text-gray-800">Desconto</span>
            <InlineDiscountBadge
              value={item.discountPercent}
              field="discountPercent"
              onSave={onUpdateDiscount}
              disabled={isUpdatingDiscount}
            />
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 text-muted-foreground hover:text-blue-600"
              onClick={onEdit}
            >
              <Pencil className="h-3.5 w-3.5" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 text-muted-foreground hover:text-destructive"
              onClick={onRemove}
              disabled={isRemoving}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>

        {/* Linha Quantidade */}
        <div className="grid grid-cols-[45%_55%] items-center bg-white px-4 py-2 border-b border-gray-200 text-sm divide-x divide-gray-200">
          <span className="font-medium text-gray-800 pr-4">Quantidade</span>
          <span className="text-gray-900 pl-4">{item.quantity}</span>
        </div>

        {/* Linha Preço Unitário */}
        {hasAnyUnitPrice && (
          <div className="grid grid-cols-[45%_55%] items-center bg-white px-4 py-2 border-b border-gray-200 text-sm divide-x divide-gray-200">
            <span className="font-medium text-gray-800 pr-4">
              {renderLabelWithRange(`Preço unitário ${unitLabel}`, quantityRangeLabel)}
            </span>
            <span className="text-gray-900 pl-4">{unitPriceDisplay}</span>
          </div>
        )}

        {/* Linha Subtotal */}
        {hasAnySubtotal && (
          <div className="grid grid-cols-[45%_55%] items-center bg-white px-4 py-2 border-b border-gray-200 text-sm divide-x divide-gray-200">
            <span className="font-medium text-gray-800 pr-4">
              {renderLabelWithRange(`Subtotal ${subLabel}`, quantityRangeLabel)}
            </span>
            <span className="text-gray-900 pl-4">{subPriceDisplay}</span>
          </div>
        )}

        {/* Linha Conectividade */}
        {hasConnectivity && (
          <>
            <div className="grid grid-cols-[45%_1fr_auto] items-center bg-gray-50 px-4 py-2 border-b border-gray-200 text-sm divide-x divide-gray-200">
              <span className="font-semibold text-gray-900 pr-4">Conectividade</span>
              <div className="flex items-center gap-2 pl-4">
                <span className="text-sm font-medium text-gray-800">Desconto</span>
                <InlineDiscountBadge
                  value={item.connectivityDiscountPercent}
                  field="connectivityDiscountPercent"
                  onSave={onUpdateDiscount}
                  disabled={isUpdatingDiscount}
                />
              </div>
              <span />
            </div>

            {/* Linha Operadora */}
            <div className="grid grid-cols-[45%_55%] items-center bg-white px-4 py-2 border-b border-gray-200 text-sm divide-x divide-gray-200">
              <span className="font-medium text-gray-800 pr-4">Operadora</span>
              <span className="text-gray-900 pl-4">
                {conn?.carrier}
                {conn?.planType
                  ? ` - ${formatDataAmount(conn?.dataAmountMb)} - ${conn?.planType}`
                  : ''}
              </span>
            </div>

            {/* Linha Preço Mensal */}
            {hasConnMonthlyPrice && (
              <div className="grid grid-cols-[45%_55%] items-center bg-white px-4 py-2 text-sm divide-x divide-gray-200">
                <span className="font-medium text-gray-800 pr-4">
                  Preço mensal unitário / total
                </span>
                <span className="text-gray-900 pl-4">
                  {renderWithDiscount(
                    `${formatCents(connMonthlyUnitPrice)} / ${formatCents(connMonthlyUnitPrice * item.quantity)}`,
                    `${formatCents(connMonthlyDiscountedUnit)} / ${formatCents(connMonthlySubtotal)}`,
                    monthlyDiscountPercent > 0,
                  )}
                </span>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
